/** F-41: avatar stack of everyone on the board (self + remote peers). */
import type { PresenceUser } from "@kola/shared";

import { initialsFor } from "../../core/identity.js";
import type { RemotePeer } from "../../core/session.js";

const MAX_AVATARS = 8;

export interface AvatarStackProps {
  self: PresenceUser;
  peers: readonly RemotePeer[];
}

export function AvatarStack({ self, peers }: AvatarStackProps) {
  // One avatar per user even when they have several tabs/connections open.
  const seen = new Set<string>([self.id]);
  const users: { user: PresenceUser; isSelf: boolean }[] = [{ user: self, isSelf: true }];
  for (const peer of peers) {
    if (seen.has(peer.user.id)) continue;
    seen.add(peer.user.id);
    users.push({ user: peer.user, isSelf: false });
  }

  const shown = users.slice(0, MAX_AVATARS);
  const overflow = users.length - shown.length;

  return (
    <div className="avatar-stack" aria-label={`${users.length} users on this board`}>
      {shown.map(({ user, isSelf }) => (
        <div
          key={user.id}
          className={isSelf ? "avatar self" : "avatar"}
          style={{ background: user.color }}
          data-tip={isSelf ? `${user.name} (you)` : user.name}
        >
          {initialsFor(user.name)}
        </div>
      ))}
      {overflow > 0 && <div className="avatar-overflow">+{overflow}</div>}
    </div>
  );
}
